import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { useGradeFlow } from '@/states/grade-flow';

import { SegmentationModal } from './segmentation-images/segmentation-modal';

type InsightsThumbnailsProps = {
  filter?: 'all' | 'left' | 'right';
  size?: 'sm' | 'md';
};

const SIZE_MAP_STYLE = {
  sm: 'xl:h-24',
  md: 'xl:h-40',
};

export function InsightsThumbnails({ filter = 'all', size = 'md' }: InsightsThumbnailsProps) {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | undefined>();

  const { images, caseId } = useGradeFlow(
    useShallow((s) => ({
      images: s.images,
      caseId: s.case?.case_id,
    }))
  );

  // Filter images based on laterality
  const imagesFiltered = useMemo(() => {
    if (filter === 'all') {
      return images ?? [];
    }

    return images?.filter((i) => i.lat === filter) ?? [];
  }, [images, filter]);

  const handleImageClick = (imageName: string) => {
    setSelectedImage(imageName);
    setModalOpen(true);
  };

  if (!imagesFiltered?.length) {
    return <p className="pb-4 text-gray-500 text-sm">{t('grade-no-image-available')}</p>;
  }

  if (!caseId) {
    return <p className="pb-4 text-gray-500 text-sm">No case available</p>;
  }

  return (
    <>
      <div className="flex-full-center gap-4 overflow-x-auto">
        {imagesFiltered.map((image, index) => (
          <div key={`insights-image-${index}`} className="cursor-pointer">
            <img
              className={`${SIZE_MAP_STYLE[size]} object-scale-down hover:opacity-80 transition-opacity`}
              src={image.url}
              alt={`Case img ${index + 1}`}
              onClick={() => handleImageClick(image.image)}
            />
          </div>
        ))}
      </div>
      <SegmentationModal
        showActions={false}
        filter={filter}
        caseId={caseId}
        open={modalOpen}
        onOpenChange={setModalOpen}
        initialImage={selectedImage}
      />
    </>
  );
}
